import { useCallback, useRef } from 'react';
import { useEntityHierarchy } from './useEntityLookup';
import { EntityHierarchy } from '../services/entityLookupService';
import { EntitySelectorConfig } from '../types/formBuilder';

interface UseEntityAutoMappingOptions {
  programId: string | undefined;
  entityType: string | undefined;
  entityId: string | undefined;
  config: EntitySelectorConfig | undefined;
  onMappedFieldsResolved?: (mappedValues: Record<string, string>) => void;
}

/**
 * Hook that resolves hierarchy when an entity is selected and
 * produces auto-mapped field values for dependent fields.
 */
export function useEntityAutoMapping({
  programId,
  entityType,
  entityId,
  config,
  onMappedFieldsResolved,
}: UseEntityAutoMappingOptions) {
  const lastResolvedRef = useRef<string | null>(null);

  const shouldResolve =
    !!config?.autoMappingEnabled &&
    !!entityId &&
    entityId !== lastResolvedRef.current;

  const { data: hierarchy, isLoading } = useEntityHierarchy(
    programId,
    entityType,
    entityId,
    shouldResolve
  );

  // Compute mapped values from hierarchy
  const getMappedValues = useCallback(
    (resolved: EntityHierarchy): Record<string, string> => {
      if (!config?.autoMappedFields || config.autoMappedFields.length === 0) {
        return {};
      }

      const result: Record<string, string> = {};
      for (const field of config.autoMappedFields) {
        const value = resolveFieldFromHierarchy(field, resolved);
        if (value) {
          result[field] = value;
        }
      }
      return result;
    },
    [config?.autoMappedFields]
  );

  // When hierarchy resolves, notify parent
  if (hierarchy && entityId && entityId !== lastResolvedRef.current) {
    lastResolvedRef.current = entityId;
    const mapped = getMappedValues(hierarchy);
    onMappedFieldsResolved?.(mapped);
  }

  return {
    hierarchy,
    isResolving: isLoading,
    mappedValues: hierarchy ? getMappedValues(hierarchy) : {},
  };
}

function resolveFieldFromHierarchy(
  fieldName: string,
  hierarchy: EntityHierarchy
): string | undefined {
  const normalized = fieldName.toLowerCase().replace(/_/g, '');
  switch (normalized) {
    case 'hospitalid':
      return hierarchy.hospitalId;
    case 'hospitalname':
      return hierarchy.hospitalName;
    case 'hospitalcode':
      return hierarchy.hospitalCode;
    case 'hcpid':
      return hierarchy.hcpId;
    case 'hcpname':
      return hierarchy.hcpName;
    case 'hcpcode':
      return hierarchy.hcpCode;
    case 'patientid':
      return hierarchy.patientId;
    case 'patientname':
      return hierarchy.patientName;
    case 'patientcode':
      return hierarchy.patientCode;
    case 'caseid':
      return hierarchy.caseId;
    case 'casenumber':
      return hierarchy.caseNumber;
    case 'programid':
      return hierarchy.programId;
    case 'programname':
      return hierarchy.programName;
    default:
      return undefined;
  }
}
